import { folderTakeovers } from "./board-selection"
import { shortFolderName } from "./build-assignment-board"

// Rule conditions that depend on where the work is, so routing by site is what they do
const SITE_FIELDS = new Set(["facility", "facilityName", "facilityTown", "facilityPostcode", "town", "postcode"])

/**
 * What assigning the current selection would do, for the selection bar: the scope in words,
 * and the conditional skill rules its work would ignore. The trade is not chosen yet, so every
 * rule on a covered skill counts. Each rule only fires for some tasks (a site, a
 * classification), so the bar says the work ignores them rather than that they would apply.
 *
 * @param {object[]} targets - selectionTargets(...): { kind: "folder" | "schedule", targetId, name }
 * @param {{ allGroups: object[], schedulesById: Map, linesBySkill: Map, folderRouted: object[], folderAssignments: Map }} board
 * @returns {{ scope: string, rules: Array<{ name: string, siteBased: boolean }>, rulesText: string }}
 *   rulesText is empty when nothing is overridden
 */
export function describeSelectionScope(targets, board) {
    const rules = overriddenRules(targets, board)
    return { scope: describeScope(targets), rules, rulesText: describeRules(rules) }
}

function describeScope(targets) {
    const folders = targets.filter(isFolder)
    const scheduleCount = targets.length - folders.length
    const folderPart = describeFolders(folders)
    if (!folderPart) return `Assign ${scheduleCount} ${"schedule".pluralize(scheduleCount)}.`
    if (!scheduleCount) return `Assign ${folderPart}.`
    return `Assign ${folderPart}, and ${scheduleCount} more ${"schedule".pluralize(scheduleCount)}.`
}

function describeFolders(folders) {
    if (!folders.length) return ""
    if (folders.length === 1)
        return `the whole ${shortFolderName(folders[0].name)} folder, including schedules added later`
    return `${folders.length} whole folders, including schedules added to them later`
}

function describeRules(rules) {
    if (!rules.length) return ""
    const site = rules.some(isSiteBased) ? ", including site-based routing" : ""
    return `Ignores ${rules.length} conditional skill ${"rule".pluralize(rules.length)}${site}.`
}

function overriddenRules(targets, board) {
    const found = new Map()
    for (const schedule of coveredSchedules(targets, board)) {
        for (const { skillId } of schedule.skills || []) {
            for (const rule of board.linesBySkill.get(String(skillId))?.conditionalTrades || []) {
                if (!rule?.name) continue
                const siteBased = (rule.conditions || []).some(isSiteCondition)
                found.set(rule.name, { name: rule.name, siteBased: siteBased || !!found.get(rule.name)?.siteBased })
            }
        }
    }
    return [...found.values()].sort(byRuleName)
}

// The schedules the selection would route: those ticked, those filed in a ticked folder (in any
// of their library placements, not only the folder they are listed under, as the selection
// itself counts them), and those another folder's assignment routes today that a ticked folder
// would take over
function coveredSchedules(targets, board) {
    const covered = new Map()
    const folderIds = new Set(targets.filter(isFolder).map(toTargetId))
    for (const target of targets) {
        if (target.kind === "schedule") add(board.schedulesById.get(target.targetId))
    }
    for (const group of board.allGroups || []) {
        group.schedules.filter(isInTickedFolder).forEach(add)
    }
    for (const { scheduleId } of folderTakeovers(board, targets)) add(board.schedulesById.get(scheduleId))
    return [...covered.values()]

    function add(schedule) {
        if (schedule) covered.set(schedule.scheduleId, schedule)
    }

    function isInTickedFolder(schedule) {
        return (schedule.folderIds || []).some(isTickedFolder)
    }

    function isTickedFolder(folderId) {
        return folderIds.has(String(folderId))
    }
}

function isFolder(target) {
    return target.kind === "folder"
}

function toTargetId(target) {
    return String(target.targetId)
}

function isSiteCondition(condition) {
    return SITE_FIELDS.has(condition?.field)
}

function isSiteBased(rule) {
    return rule.siteBased
}

function byRuleName(a, b) {
    return a.name.localeCompare(b.name)
}
